<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { areLabelStageMappingsValid } from './labelStageMappingValidation';

const props = defineProps({
  pipeline: {
    type: Object,
    required: true,
  },
  stages: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:mappings', 'update:valid']);

const { t } = useI18n();
const store = useStore();

// Track initialization state to prevent race conditions
const isInitialized = ref(false);
const lastPipelineId = ref(null);

// Get labels from store
const labels = computed(() => store.getters['labels/getLabels'] || []);
const labelsLoading = ref(false);
const labelsError = ref(false);

// Initialize mappings from pipeline settings
const labelStageMappings = ref([]);
const stageLabelMappings = ref([]);

// Actions for label->stage mappings
const labelStageActions = [
  { value: 'move', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MOVE') },
  { value: 'mark_won', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MARK_WON') },
  {
    value: 'mark_lost',
    label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.MARK_LOST'),
  },
];

// Actions for stage->label mappings
const stageLabelActions = [
  { value: 'add', label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.ADD_LABEL') },
  {
    value: 'remove',
    label: t('PIPELINE.LABEL_MAPPINGS.ACTIONS.REMOVE_LABEL'),
  },
];

// All stages including won/lost
const allStages = computed(() => props.stages);

const hasInvalidMappings = computed(
  () =>
    !areLabelStageMappingsValid({
      mappings: {
        label_stage_mappings: labelStageMappings.value,
        stage_label_mappings: stageLabelMappings.value,
      },
      stages: props.stages,
      labels: labels.value,
    })
);

// Check if a stage exists in current stages list
const stageExists = stageId => {
  if (!stageId) return true; // Empty is valid (not yet selected)
  return props.stages.some(s => String(s.id) === String(stageId));
};

// Check if a label exists in current labels list
const labelExists = labelTitle => {
  if (!labelTitle) return true; // Empty is valid (not yet selected)
  return labels.value.some(
    l =>
      String(l.title || '').toLocaleLowerCase() ===
      String(labelTitle).toLocaleLowerCase()
  );
};
const labelOptionsFor = currentValue => {
  const options = [
    { value: '', label: t('PIPELINE.LABEL_MAPPINGS.SELECT_LABEL') },
  ];
  if (currentValue && !labelExists(currentValue)) {
    options.push({
      value: currentValue,
      label: t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
        name: currentValue,
      }),
    });
  }
  return [
    ...options,
    ...labels.value.map(label => ({
      value: label.title,
      label: label.title,
    })),
  ];
};
const stageOptionsFor = currentValue => {
  const options = [
    { value: '', label: t('PIPELINE.LABEL_MAPPINGS.SELECT_STAGE') },
  ];
  if (currentValue && !stageExists(currentValue)) {
    options.push({
      value: currentValue,
      label: t('PIPELINE.LABEL_MAPPINGS.DELETED_ITEM', {
        name: currentValue,
      }),
    });
  }
  return [
    ...options,
    ...allStages.value.map(stage => ({
      value: stage.id,
      label: stage.name,
    })),
  ];
};

// Validate mappings before emitting - returns cleaned data
const validateMappings = mappingsData => {
  const cleanedLabelStage = mappingsData.label_stage_mappings.map(m => ({
    ...m,
    // Ensure required fields have defaults
    id:
      m.id ||
      `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    enabled: m.enabled !== undefined ? m.enabled : true,
    action: m.action || 'move',
  }));

  const cleanedStageLabel = mappingsData.stage_label_mappings.map(m => ({
    ...m,
    id:
      m.id ||
      `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
    enabled: m.enabled !== undefined ? m.enabled : true,
    action: m.action || 'add',
  }));

  return {
    label_stage_mappings: cleanedLabelStage,
    stage_label_mappings: cleanedStageLabel,
  };
};

// Emit update to parent with validation
const emitUpdate = () => {
  if (!isInitialized.value) return;

  const validatedData = validateMappings({
    label_stage_mappings: labelStageMappings.value,
    stage_label_mappings: stageLabelMappings.value,
  });

  emit('update:mappings', validatedData);
};

const initializeMappings = () => {
  const settings = props.pipeline?.settings || {};
  labelStageMappings.value = Array.from(
    settings.label_stage_mappings || [],
    mapping => ({ ...mapping })
  );
  stageLabelMappings.value = Array.from(
    settings.stage_label_mappings || [],
    mapping => ({ ...mapping })
  );
};

const initializeForPipeline = () => {
  isInitialized.value = false;
  initializeMappings();
  lastPipelineId.value = props.pipeline?.id;
  isInitialized.value = true;
  emit('update:valid', !hasInvalidMappings.value);
};

const fetchLabels = async () => {
  labelsLoading.value = true;
  labelsError.value = false;
  try {
    await store.dispatch('labels/get');
  } catch {
    labelsError.value = true;
  } finally {
    labelsLoading.value = false;
  }
};

initializeForPipeline();

// Fetch labels on mount. The initial validity is emitted synchronously above;
// the validity watcher emits again as soon as the account label catalog lands.
onMounted(fetchLabels);

// Watch for pipeline changes - only reinitialize when pipeline ID changes
// This prevents race conditions when user is editing mappings
watch(
  () => props.pipeline?.id,
  newId => {
    if (newId !== lastPipelineId.value) {
      initializeForPipeline();
    }
  }
);

watch(
  hasInvalidMappings,
  invalid => {
    if (isInitialized.value) emit('update:valid', !invalid);
  },
  { flush: 'sync' }
);

// Generate unique ID
const generateId = () => {
  return `mapping_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

// Add new label->stage mapping
const addLabelStageMapping = () => {
  if (props.readonly) return;
  labelStageMappings.value.push({
    id: generateId(),
    label_title: '',
    target_stage: '',
    action: 'move',
    lost_reason: '',
    enabled: true,
  });
  emitUpdate();
};

// Add new stage->label mapping
const addStageLabelMapping = () => {
  if (props.readonly) return;
  stageLabelMappings.value.push({
    id: generateId(),
    stage: '',
    label_title: '',
    action: 'add',
    enabled: true,
  });
  emitUpdate();
};

// Remove label->stage mapping
const removeLabelStageMapping = index => {
  if (props.readonly) return;
  labelStageMappings.value.splice(index, 1);
  emitUpdate();
};

// Remove stage->label mapping
const removeStageLabelMapping = index => {
  if (props.readonly) return;
  stageLabelMappings.value.splice(index, 1);
  emitUpdate();
};

// Update mapping enabled state
const updateLabelStageEnabled = (index, enabled) => {
  if (props.readonly) return;
  labelStageMappings.value[index].enabled = enabled;
  emitUpdate();
};

const updateStageLabelEnabled = (index, enabled) => {
  if (props.readonly) return;
  stageLabelMappings.value[index].enabled = enabled;
  emitUpdate();
};

// Handle mapping field change
const onMappingChange = () => {
  if (props.readonly) return;
  emitUpdate();
};
</script>

<template>
  <div class="space-y-6">
    <div
      v-if="labelsLoading"
      role="status"
      class="flex min-h-20 items-center justify-center gap-2 text-sm text-n-slate-11"
    >
      <span
        class="size-5 motion-safe:animate-spin rounded-full border-2 border-n-weak border-t-n-brand"
      />
      {{ t('PIPELINE.LABEL_MAPPINGS.LOADING_LABELS') }}
    </div>
    <div
      v-else-if="labelsError"
      role="alert"
      class="flex flex-col items-start gap-3 rounded-lg border border-n-ruby-8 bg-n-ruby-3 p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <span class="text-sm text-n-ruby-11">
        {{ t('PIPELINE.LABEL_MAPPINGS.LOAD_ERROR') }}
      </span>
      <button
        type="button"
        class="min-h-11 rounded-lg px-3 text-sm font-medium text-n-ruby-11 outline outline-1 outline-n-ruby-8 hover:bg-n-ruby-4 focus-visible:outline-2"
        @click="fetchLabels"
      >
        {{ t('PIPELINE.LABEL_MAPPINGS.RETRY') }}
      </button>
    </div>

    <!-- Label -> Stage Mappings -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.LABEL_MAPPINGS.LABEL_TO_STAGE.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('PIPELINE.LABEL_MAPPINGS.LABEL_TO_STAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Mapping rules list -->
      <div class="space-y-2">
        <div
          v-for="(mapping, index) in labelStageMappings"
          :key="mapping.id"
          class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div
            class="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start"
          >
            <!-- Enable toggle -->
            <Switch
              :model-value="mapping.enabled"
              :disabled="readonly"
              :aria-label="
                t('PIPELINE.LABEL_MAPPINGS.LABEL_TO_STAGE.ENABLE_RULE', {
                  index: index + 1,
                })
              "
              class="sm:mt-6"
              @update:model-value="
                value => updateLabelStageEnabled(index, value)
              "
            />

            <!-- Fields grid -->
            <div
              class="grid flex-1 grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-3"
            >
              <!-- Label select -->
              <div>
                <label
                  :for="`label-stage-label-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.LABEL') }}
                </label>
                <Select
                  v-model="mapping.label_title"
                  :input-id="`label-stage-label-${mapping.id}`"
                  :name="`labelStageLabel-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="labelOptionsFor(mapping.label_title)"
                  :disabled="readonly || labelsLoading"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.LABEL')"
                  :error="
                    !labelExists(mapping.label_title) || !mapping.label_title
                      ? t('PIPELINE.LABEL_MAPPINGS.SELECT_LABEL')
                      : ''
                  "
                  @update:model-value="onMappingChange"
                />
              </div>

              <!-- Action select -->
              <div>
                <label
                  :for="`label-stage-action-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.ACTION') }}
                </label>
                <Select
                  v-model="mapping.action"
                  :input-id="`label-stage-action-${mapping.id}`"
                  :name="`labelStageAction-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="labelStageActions"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.ACTION')"
                  :disabled="readonly"
                  @update:model-value="onMappingChange"
                />
              </div>

              <!-- Stage select (only for move action) -->
              <div v-if="mapping.action === 'move'">
                <label
                  :for="`label-stage-target-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.STAGE') }}
                </label>
                <Select
                  v-model="mapping.target_stage"
                  :input-id="`label-stage-target-${mapping.id}`"
                  :name="`labelStageTarget-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="stageOptionsFor(mapping.target_stage)"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.STAGE')"
                  :disabled="readonly"
                  :error="
                    !stageExists(mapping.target_stage) || !mapping.target_stage
                      ? t('PIPELINE.LABEL_MAPPINGS.SELECT_STAGE')
                      : ''
                  "
                  @update:model-value="onMappingChange"
                />
              </div>

              <!-- Lost reason (only for mark_lost action) -->
              <div v-if="mapping.action === 'mark_lost'">
                <label
                  :for="`label-stage-reason-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.LOST_REASON') }}
                </label>
                <Input
                  :id="`label-stage-reason-${mapping.id}`"
                  v-model="mapping.lost_reason"
                  :name="`labelStageReason-${mapping.id}`"
                  :disabled="readonly"
                  type="text"
                  class="w-full"
                  custom-input-class="!h-11"
                  :placeholder="
                    t('PIPELINE.LABEL_MAPPINGS.LOST_REASON_PLACEHOLDER')
                  "
                  @input="onMappingChange"
                />
              </div>
            </div>

            <!-- Remove button -->
            <button
              type="button"
              class="flex size-11 shrink-0 items-center justify-center self-end rounded-lg text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-ruby-9 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-5"
              :disabled="readonly"
              :aria-label="t('PIPELINE.LABEL_MAPPINGS.REMOVE_RULE')"
              @click="removeLabelStageMapping(index)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>

        <!-- Add button -->
        <button
          type="button"
          class="flex min-h-11 items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 rounded-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="readonly"
          @click="addLabelStageMapping"
        >
          <fluent-icon icon="add" size="16" />
          {{ t('PIPELINE.LABEL_MAPPINGS.ADD_LABEL_STAGE_RULE') }}
        </button>
      </div>
    </div>

    <!-- Divider -->
    <hr class="border-n-weak" />

    <!-- Stage -> Label Mappings -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('PIPELINE.LABEL_MAPPINGS.STAGE_TO_LABEL.TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{ t('PIPELINE.LABEL_MAPPINGS.STAGE_TO_LABEL.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Mapping rules list -->
      <div class="space-y-2">
        <div
          v-for="(mapping, index) in stageLabelMappings"
          :key="mapping.id"
          class="p-3 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <div
            class="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start"
          >
            <!-- Enable toggle -->
            <Switch
              :model-value="mapping.enabled"
              :disabled="readonly"
              :aria-label="
                t('PIPELINE.LABEL_MAPPINGS.STAGE_TO_LABEL.ENABLE_RULE', {
                  index: index + 1,
                })
              "
              class="sm:mt-6"
              @update:model-value="
                value => updateStageLabelEnabled(index, value)
              "
            />

            <!-- Fields grid -->
            <div
              class="grid flex-1 grid-cols-1 items-end gap-2 sm:grid-cols-2 lg:grid-cols-3"
            >
              <!-- Stage select -->
              <div>
                <label
                  :for="`stage-label-stage-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.STAGE') }}
                </label>
                <Select
                  v-model="mapping.stage"
                  :input-id="`stage-label-stage-${mapping.id}`"
                  :name="`stageLabelStage-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="stageOptionsFor(mapping.stage)"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.STAGE')"
                  :disabled="readonly"
                  :error="
                    !stageExists(mapping.stage) || !mapping.stage
                      ? t('PIPELINE.LABEL_MAPPINGS.SELECT_STAGE')
                      : ''
                  "
                  @update:model-value="onMappingChange"
                />
              </div>

              <!-- Action select -->
              <div>
                <label
                  :for="`stage-label-action-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.ACTION') }}
                </label>
                <Select
                  v-model="mapping.action"
                  :input-id="`stage-label-action-${mapping.id}`"
                  :name="`stageLabelAction-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="stageLabelActions"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.ACTION')"
                  :disabled="readonly"
                  @update:model-value="onMappingChange"
                />
              </div>

              <!-- Label select -->
              <div>
                <label
                  :for="`stage-label-label-${mapping.id}`"
                  class="block text-xs text-n-slate-11 mb-1"
                >
                  {{ t('PIPELINE.LABEL_MAPPINGS.LABEL') }}
                </label>
                <Select
                  v-model="mapping.label_title"
                  :input-id="`stage-label-label-${mapping.id}`"
                  :name="`stageLabelLabel-${mapping.id}`"
                  class="w-full [&>select]:w-full"
                  :options="labelOptionsFor(mapping.label_title)"
                  :aria-label="t('PIPELINE.LABEL_MAPPINGS.LABEL')"
                  :disabled="readonly || labelsLoading"
                  :error="
                    !labelExists(mapping.label_title) || !mapping.label_title
                      ? t('PIPELINE.LABEL_MAPPINGS.SELECT_LABEL')
                      : ''
                  "
                  @update:model-value="onMappingChange"
                />
              </div>
            </div>

            <!-- Remove button -->
            <button
              type="button"
              class="flex size-11 shrink-0 items-center justify-center self-end rounded-lg text-n-slate-10 hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-ruby-9 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-5"
              :disabled="readonly"
              :aria-label="t('PIPELINE.LABEL_MAPPINGS.REMOVE_RULE')"
              @click="removeStageLabelMapping(index)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>

        <!-- Add button -->
        <button
          type="button"
          class="flex min-h-11 items-center gap-2 px-3 py-2 text-sm text-n-brand hover:bg-n-alpha-1 rounded-lg transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="readonly"
          @click="addStageLabelMapping"
        >
          <fluent-icon icon="add" size="16" />
          {{ t('PIPELINE.LABEL_MAPPINGS.ADD_STAGE_LABEL_RULE') }}
        </button>
      </div>
    </div>

    <!-- Info box -->
    <div class="p-3 bg-n-blue-3 border border-n-blue-9 rounded-lg">
      <div class="flex items-start gap-2">
        <fluent-icon
          icon="info"
          size="16"
          class="text-n-blue-11 mt-0.5 flex-shrink-0"
        />
        <div class="text-xs text-n-blue-11 space-y-1">
          <p>{{ t('PIPELINE.LABEL_MAPPINGS.INFO.LINE_1') }}</p>
          <p>{{ t('PIPELINE.LABEL_MAPPINGS.INFO.LINE_2') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>
